import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { setupGuard } from './guard'

// 扩展路由 meta 类型
declare module 'vue-router' {
  interface RouteMeta {
    /** 页面标题 */
    title?: string
    /** 是否需要登录（默认 true） */
    requiresAuth?: boolean
    /** 页面所属应用入口。 */
    requiredApplication?: 'frontend' | 'admin'
    /** 页面至少或全部需要的权限点。 */
    requiredPermissions?: string[]
    /** 多权限点校验方式，默认 any。 */
    permissionMode?: 'any' | 'all'
    /** 访问状态非 active 时仍允许进入的认证页面。 */
    allowBlockedWorkspaceAccess?: boolean
    /** 仅默认公司 C 端可进入的商业或探索页面。 */
    consumerOnly?: boolean
    /** 仅班主任可进入的轻量工作空间页面。 */
    homeroomOnly?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import('@/views/home/index.vue'),
        meta: { title: '首页', requiresAuth: false },
      },
      {
        path: '/about',
        name: 'About',
        component: () => import('@/views/about/index.vue'),
        meta: { title: '关于我们', requiresAuth: false },
      },
      {
        path: '/help',
        name: 'Help',
        component: () => import('@/views/help/index.vue'),
        meta: { title: '帮助中心', requiresAuth: false },
      },
      {
        path: '/contact',
        name: 'Contact',
        component: () => import('@/views/contact/index.vue'),
        meta: { title: '联系我们', requiresAuth: false },
      },
      {
        path: '/privacy',
        name: 'Privacy',
        component: () => import('@/views/privacy/index.vue'),
        meta: { title: '隐私政策', requiresAuth: false },
      },
      {
        path: '/feynman',
        name: 'Feynman',
        component: () => import('@/views/feynman/index.vue'),
        meta: { title: '费曼星球', requiresAuth: false },
      },
    ],
  },
  {
    path: '/auth',
    component: () => import('@/layouts/BlankLayout.vue'),
    children: [
      {
        path: '',
        name: 'Auth',
        component: () => import('@/views/auth/index.vue'),
        meta: { title: '登录', requiresAuth: false },
      },
      {
        path: 'join/:invitationToken',
        name: 'AuthInvitation',
        component: () => import('@/views/auth/index.vue'),
        meta: { title: '注册并加入工作空间', requiresAuth: false },
      },
    ],
  },
  {
    path: '/auth/sso',
    name: 'SsoRedeem',
    component: () => import('@/views/auth/sso.vue'),
    meta: { title: '跨应用登录', requiresAuth: false },
  },
  { path: '/login', redirect: '/auth' },
  { path: '/register', redirect: '/auth?tab=register' },
  {
    path: '/workspace/status',
    name: 'WorkspaceStatus',
    component: () => import('@/views/workspace-status/index.vue'),
    meta: {
      title: '工作空间状态',
      allowBlockedWorkspaceAccess: true,
    },
  },
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: () => import('@/views/access-denied/index.vue'),
    meta: { title: '访问受限' },
  },
  {
    path: '/payment-result',
    name: 'PaymentResult',
    component: () => import('@/views/payment-result/index.vue'),
    meta: { title: '支付结果', requiresAuth: false, consumerOnly: true },
  },
  {
    path: '/explore',
    component: () => import('@/layouts/LearningLayout.vue'),
    meta: { requiredApplication: 'frontend', consumerOnly: true },
    children: [
      {
        path: '',
        name: 'Explore',
        component: () => import('@/views/explore/index.vue'),
        meta: { title: '探索星球' },
      },
    ],
  },
  {
    path: '/learning',
    component: () => import('@/layouts/LearningLayout.vue'),
    redirect: '/learning/repo',
    meta: { requiredApplication: 'frontend' },
    children: [
      {
        path: 'workspace',
        name: 'SchoolResourceLanding',
        component: () => import('@/views/learning/school-resources/index.vue'),
        meta: { title: '校本资源', requiredApplication: 'frontend' },
      },
      {
        path: ':source(repo|teacher)',
        name: 'DisciplineLanding',
        component: () => import('@/views/learning/discipline-landing/index.vue'),
        meta: { title: '学习中心', requiresAuth: false },
      },
      {
        path: ':source(repo|teacher)/:category(subject|ability)',
        name: 'CourseList',
        component: () => import('@/views/learning/course-list/index.vue'),
        meta: { title: '学习中心', requiresAuth: false },
      },
      {
        path: ':source(repo|teacher)/:category(subject|ability)/:subject',
        name: 'CourseListBySubject',
        component: () => import('@/views/learning/course-list/index.vue'),
        meta: { title: '学习中心', requiresAuth: false },
      },
      {
        path: 'packages/:packageId',
        name: 'LearningPackageDetail',
        component: () => import('@/views/learning/package-detail/index.vue'),
        meta: { title: '课包详情', requiresAuth: false },
      },
      {
        path: 'points/:pointId',
        name: 'LearningPointDetail',
        component: () => import('@/views/learning/point-detail/index.vue'),
        meta: { title: '知识点详情', requiresAuth: false },
      },
      {
        path: 'my/progress',
        name: 'MyProgress',
        component: () => import('@/views/learning/my-progress/index.vue'),
        meta: { title: '我的学习', requiredApplication: 'frontend' },
      },
      {
        path: 'my/history',
        name: 'MyHistory',
        component: () => import('@/views/learning/my-history/index.vue'),
        meta: { title: '学习记录', requiredApplication: 'frontend' },
      },
      {
        path: 'my/tasks',
        name: 'MyLearningTasks',
        component: () => import('@/views/learning/my-tasks/index.vue'),
        meta: { title: '学习任务', requiredApplication: 'frontend' },
      },
      {
        path: 'my/tasks/:taskId',
        name: 'MyLearningTaskDetail',
        component: () => import('@/views/learning/task-detail/index.vue'),
        meta: { title: '任务学习', requiredApplication: 'frontend' },
      },
      {
        path: 'my/transfer-applications',
        name: 'MyTransferApplications',
        component: () => import('@/views/learning/transfer-applications/index.vue'),
        meta: {
          title: '转班申请',
          requiredApplication: 'frontend',
          homeroomOnly: true,
        },
      },
      {
        path: 'payments',
        name: 'LearningPayments',
        component: () => import('@/views/learning/payments/index.vue'),
        meta: { title: '星球商店', requiredApplication: 'frontend', consumerOnly: true },
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/learning/profile/index.vue'),
        meta: { title: '个人中心', requiredApplication: 'frontend' },
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  /** 路由切换时滚动到顶部 */
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

setupGuard(router)

export default router
